/**
 * Slug helpers for LookBel's public SEO URLs.
 *
 * Slugs are always derived from real marketplace data (a real city name, a real
 * stylist name). We never reverse a slug back into display copy — a slug is
 * lossy ("st-louis" could be "St. Louis" or "St Louis"), so display strings
 * always come from the API payload, and slugs only ever flow data -> URL.
 *
 * CommonJS on purpose — shared by the CRA bundle and the Node build scripts.
 */

/** Slugs we accept in a URL segment: lowercase alphanumerics separated by single hyphens. */
const SLUG_SEGMENT_REGEX = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/**
 * Lowercase, ASCII-fold, strip punctuation, collapse to single hyphens.
 *
 * @param {string | null | undefined} value
 * @returns {string} slug, or '' when nothing usable remains
 */
function slugify(value) {
  if (value == null) return '';
  return String(value)
    .normalize('NFKD')
    // Drop combining marks so "Beyoncé" -> "beyonce".
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/['\u2019]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-{2,}/g, '-');
}

/**
 * @param {string | null | undefined} value
 * @returns {boolean}
 */
function isValidSlugSegment(value) {
  return typeof value === 'string' && SLUG_SEGMENT_REGEX.test(value);
}

/**
 * City slug for `/locations/:state/:city`.
 *
 * @param {string | null | undefined} city
 * @returns {string}
 */
function citySlug(city) {
  return slugify(city);
}

/**
 * Shape check for the `seoSlug` the backend assigns to stylists:
 * `<name>-<city>-<state>-<shortCode>`, e.g. `jordan-blake-austin-tx-h7k9m2xq`.
 * The trailing segment is the stylist's existing unique shortCode (lowercased),
 * not a freshly generated disambiguator — see backend/src/utils/seoSlug.js.
 * We only validate shape here; resolution is always a real API lookup.
 *
 * @param {string | null | undefined} value
 * @returns {boolean}
 */
function isStylistSeoSlug(value) {
  if (!isValidSlugSegment(value)) return false;
  // At minimum: one name part + city + state + disambiguator.
  return String(value).split('-').length >= 3 && String(value).length <= 120;
}

module.exports = {
  SLUG_SEGMENT_REGEX,
  slugify,
  isValidSlugSegment,
  citySlug,
  isStylistSeoSlug,
};
